import { createEnv } from '@t3-oss/env-core'
import { z } from 'zod'

const envResult = createEnv({
  server: {
    SERVER_URL: z.string().optional(),
  },

  /**
   * The prefix that client-side variables must have. This is enforced both at
   * a type-level and at runtime.
   */
  clientPrefix: 'VITE_',

  client: {
    VITE_APP_TITLE: z.string().min(1).optional(),
    VITE_BUILD_TIMESTAMP: z.string().optional(), // For deployment detection
    VITE_CONVEX_URL: z.url(),
    VITE_SENTRY_DSN: z.url().optional(),
    VITE_LEMONSQUEEZY_PROD_CHECKOUT_URL: z.url(),
    VITE_LEMONSQUEEZY_TEST_CHECKOUT_URL: z.url(),
    VITE_MARKETING_URL: z.url().optional(),
  },

  /**
   * What object holds the environment variables at runtime. This is usually
   * `process.env` or `import.meta.env`.
   */
  runtimeEnv: import.meta.env,

  /**
   * By default, this library will feed the environment variables directly to
   * the Zod validator.
   *
   * This means that if you have an empty string for a value that is supposed
   * to be a number (e.g. `PORT=` in a ".env" file), Zod will incorrectly flag
   * it as a type mismatch violation. Additionally, if you have an empty string
   * for a value that is supposed to be a string with a default value (e.g.
   * `DOMAIN=` in an ".env" file), the default value will never be applied.
   *
   * In order to solve these issues, we recommend that all new projects
   * explicitly specify this option as true.
   */
  emptyStringAsUndefined: true,
})

/**
 * Extended environment with computed properties
 */
export const env = {
  ...envResult,
  /**
   * LemonSqueezy checkout URL - automatically selects production or test checkout based on environment
   */
  get VITE_LEMONSQUEEZY_CHECKOUT_URL() {
    return import.meta.env.PROD
      ? envResult.VITE_LEMONSQUEEZY_PROD_CHECKOUT_URL
      : envResult.VITE_LEMONSQUEEZY_TEST_CHECKOUT_URL
  },
}
